import { Injectable } from '@angular/core';

import { IOption } from '@shared/models/icommons.model';
import { ILocationPermission, IRecordAccess, IUser, UserRole } from '@shared/models/iuser.model';
import { UserPermission } from '@shared/models/user-permissions.model';
import {
  AppStorageService,
  USER_INFO_TOKEN,
  USER_LOCATION_PERMS,
  USER_RECORD_PERM,
} from '@shared/services/app-storage.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { UserInfoKey } from '@miq/user';

/**
 * This is an array of Role Groups with which we can create custom role if the user has all the individual roles.
 * If we have roles a,b,c,d and the user has a,b,c as user roles and we needed a custom role a+c for ngxPermission,
 * we add a,c to the following array.
 */
const CUSTOM_COMBINATIONAL_GROUPS = [['Admin User', 'Quarter-Close']];

/**
 * Generic utility function to check if a user has a specific group/role
 * @param groupValue - The group value to check for
 * @param user - Optional user object, will fetch from localStorage if not provided
 * @returns boolean indicating if user has the specified group
 */
export const hasUserGroup = (groupValue: string, user?: IUser): boolean => {
  user = user ?? JSON.parse(localStorage.getItem(UserInfoKey.USERID_INFO));
  if (!user || !Array.isArray(user.groups)) {
    return false;
  }
  return user.groups.some(group => group?.value === groupValue);
};

export const isAExternalAvailabilityReviewer = (user?: IUser): boolean => {
  return hasUserGroup(userGroupEnum.EXTERNAL_AVAILABILITY_REVIEW, user);
};

export const isAnAliasManager = (user?: IUser): boolean => {
  return hasUserGroup(userGroupEnum.ALIAS_MANAGER, user);
};

export enum userGroupEnum {
  EDP_WRITE_ACCESS_GROUP_NAME = 'EDP Write Access',
  TRUSTED_USER_GROUP_NAME = 'Trusted User',
  PUBLISH_LISTINGS_GROUP_NAME = 'Publish Listings',
  WINDWARD_REPORTS_GROUP_NAME = 'Windward Reports',
  BULK_EDIT = 'Bulk Edit',
  LEASE_AGREEMENT_REVIEWER = 'Lease Agreement Reviewer',
  QC_TEAM = 'QC Team',
  EDIT_ADDRESSES = 'Edit Addresses',
  DIGITAL_ASSISTANT_GROUP_NAME = 'Digital Assistant',
  EXTERNAL_AVAILABILITY_REVIEW = 'External Availability Review',
  ALIAS_MANAGER = 'Alias Manager',
  DATA_STACK_GROUP_NAME = 'Data Stack',
}
@Injectable({
  providedIn: 'root',
})
export class UserInfoResolver extends UserPermission {
  private userInfo: IUser = null;
  private userLocationPermissons: ILocationPermission[] = null;
  private userRecordPerm: IRecordAccess[] = null;

  constructor(
    private storage: AppStorageService,
    private permissionsService: NgxPermissionsService
  ) {
    super();
  }

  public resolve(): IUser {
    if (this.userInfo == null) {
      this.userInfo = this.storage.getItem<IUser>(USER_INFO_TOKEN);
    }
    return this.userInfo;
  }

  public setUserInfo(userInfo: IUser): void {
    this.userInfo = userInfo;
    this.storage.setItem<IUser>(USER_INFO_TOKEN, this.userInfo);
  }

  public setUserLocationPerms(userLocationPermissons: ILocationPermission[]): void {
    this.userLocationPermissons = userLocationPermissons;
    this.storage.setItem(USER_LOCATION_PERMS, userLocationPermissons);
  }
  public getUserLocationPerms(): any {
    if (this.userLocationPermissons == null) {
      this.userLocationPermissons = this.storage.getItem<ILocationPermission[]>(USER_LOCATION_PERMS);
    }
    return this.userLocationPermissons;
  }

  public setUserRecordPerm(userRecordPerm: IRecordAccess[]): void {
    this.userRecordPerm = userRecordPerm;
    this.storage.setItem(USER_RECORD_PERM, userRecordPerm);
  }
  public getUserRecordPerm(): any {
    if (this.userRecordPerm == null) {
      this.userRecordPerm = this.storage.getItem<IRecordAccess[]>(USER_RECORD_PERM);
    }
    return this.userRecordPerm;
  }

  public hasEDPWriteAccess(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.EDP_WRITE_ACCESS_GROUP_NAME);
  }

  public isTrustedUser(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.TRUSTED_USER_GROUP_NAME);
  }

  public hasAEDPWriteAccess(): boolean {
    const groups = this.resolveGroups();
    return groups.includes(userGroupEnum.EDP_WRITE_ACCESS_GROUP_NAME);
  }

  public isATrustedUser(): boolean {
    const groups = this.resolveGroups();
    return groups.includes(userGroupEnum.TRUSTED_USER_GROUP_NAME);
  }

  public isInDataStackGroup(): boolean {
    const groups = this.resolveGroups();
    return groups.includes(userGroupEnum.DATA_STACK_GROUP_NAME);
  }

  public isRoleType(userRole: UserRole): boolean {
    return this.resolve()?.roleId === userRole;
  }

  public canPublishListings(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.PUBLISH_LISTINGS_GROUP_NAME);
  }

  public hasWindwardReports(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.WINDWARD_REPORTS_GROUP_NAME);
  }
  public isLeaseAgreementReviewer(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.LEASE_AGREEMENT_REVIEWER);
  }

  public isUsBroker(): boolean {
    return this.resolve()?.roleId === UserRole.USBroker;
  }

  public isInvestorBroker(): boolean {
    return this.resolve()?.roleId === UserRole.InvestorBroker;
  }

  public isOccupierBroker(): boolean {
    return this.resolve()?.roleId === UserRole.OccupierBroker;
  }

  public isUsResearcher(): boolean {
    return this.resolve()?.roleId === UserRole.USResearcher;
  }

  public isDigitalAssistantUser(): Promise<boolean> {
    return this.hasPermission(userGroupEnum.DIGITAL_ASSISTANT_GROUP_NAME);
  }
  /**
   * TODO MOVE THIS TO A DIRECTIVE
   * TO ALLOW ONLY TRUSTED USERS TO SAVE
   * UPDATE:
   * BE CAREFUL THIS LOGIC MIGHT BE CHANGED
   * BELOW DIRECTLY CHECK IF USER IS IN THE GROUP OF EDP WRITE ACCESS
   */
  hasPermission(groupName: string): Promise<boolean> {
    this.permissionsService.loadPermissions(this.resolveGroups());
    return this.permissionsService.hasPermission(groupName);
  }

  public resolveGroups() {
    const resolved = this.resolve();
    const groups = resolved ? resolved.groups : [];
    const appendedCustomGroups = !!CUSTOM_COMBINATIONAL_GROUPS.length ? this.appendCustomGroups(groups) : groups;
    return (appendedCustomGroups || []).map(({ value }) => value);
  }
  /**
   * Takes the the user roles as input, for each custom combinational group, it checks
   * whether all the user has all the roles or not. If the user has, they will be joined
   * and added to groupNames.
   *
   * @param groups: IOption[]  existing user roles
   * @returns IOption[]
   */
  public appendCustomGroups(groups: IOption[]): IOption[] {
    if (!Array.isArray(groups)) {
      return [];
    }
    const groupNames = groups?.map(({ value }) => value);
    const customGroups = CUSTOM_COMBINATIONAL_GROUPS.reduce((acc: IOption[], customGroup) => {
      const existsGroups = customGroup.filter(eachRole => groupNames?.indexOf(eachRole) > -1);
      return existsGroups.length === customGroup.length
        ? [...acc, { value: [...existsGroups].sort((a, b) => a.localeCompare(b)).join('+') }]
        : acc;
    }, []);
    return [...groups, ...customGroups];
  }

  public updateUserInfo(userInfo: Partial<IUser>): void {
    this.userInfo = { ...this.userInfo, ...userInfo };
    this.storage.setItem<IUser>(USER_INFO_TOKEN, this.userInfo);
  }

  public getUserInfoId(): number {
    return this.userInfo?.id;
  }
}
